import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';

import Form from '../../components/Form';
import { Button } from '../../components/Button';
import { useTranslation } from 'react-i18next';
import { Input } from '../../components/InputText/wrapper';
import IconAlertBox from '@components/IconAlertBox';

interface RegisterUserSchema {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  customer_number: string;
  corporate_id: string;
  status?: string;
}

const RegisterUserForm = () => {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<RegisterUserSchema>();
  const { t } = useTranslation();

  const baseUrl = new URL(import.meta.env.VITE_CUSTOMER_PORTAL_API || '');

  const url = `${baseUrl}/public/register-user`;

  const createUser = async (data: RegisterUserSchema) =>
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

  const onSubmit: SubmitHandler<RegisterUserSchema> = async (data) => {
    await createUser(data)
      .then(() => reset())
      .catch((err) => {
        setError('status', { type: 'fetch', message: `Submit failed with error: ${err.message}` });
      });
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <p className="text-large text-bold margin-bottom-8">{t('register_user_form_information')}</p>
      <Input
        {...register('firstName', { required: true })}
        className={!!errors.firstName ? 'error' : ''}
        placeholder={t('register_user_form_field_firstname')}
      />

      <Input
        {...register('lastName', { required: true })}
        className={!!errors.lastName ? 'error' : ''}
        placeholder={t('register_user_form_field_lastname')}
      />

      <Input
        {...register('email', { required: true })}
        type="email"
        className={!!errors.email ? 'error' : ''}
        placeholder={t('register_user_form_field_email')}
      />

      <Input
        {...register('phone', { pattern: /[\d\s+\-]*$/g })}
        type="tel"
        className={!!errors.phone ? 'error' : ''}
        placeholder={t('register_user_form_field_phone')}
      />

      <p className="text-large text-bold margin-bottom-8">{t('register_user_form_permissions')}</p>
      <Input
        {...register('customer_number', { required: true, pattern: /^\d*$/g })}
        className={!!errors.customer_number ? 'error' : ''}
        placeholder={t('register_user_form_field_customer_number')}
      />
      {errors.customer_number && (
        <span className="error">{t('register_user_form_field_customer_number_error')}</span>
      )}

      <Input
        {...register('corporate_id', {
          required: true,
          pattern: /^\d{6}(?:\d{2})?[-\s]?\d{4}$/,
        })}
        className={!!errors.corporate_id ? 'error' : ''}
        placeholder={t('register_user_form_field_corporate_id')}
      />
      {errors.corporate_id && (
        <span className="error">{t('register_user_form_field_corporate_id_error')}</span>
      )}

      {isSubmitSuccessful ? (
        <IconAlertBox type="success" text={t('register_user_form_submission_success')} />
      ) : (
        <Button $fullWidth disabled={isSubmitting}>
          {t('register_user_form_submission_button')}
        </Button>
      )}

      {errors.status && (
        <IconAlertBox type="error" text={t('register_user_form_submission_error')} />
      )}
    </Form>
  );
};

export default RegisterUserForm;
